/**
 * API Client Configuration
 * Base configuration for API calls
 */

import errorLoggingService from './errorLoggingService';
import {
  getAccessToken,
  getValidAccessToken,
  isTokenExpiringSoon,
  forceRefresh,
} from './tokenRefreshInterceptor';

// API Base URL from environment variable. This MUST point at the API host,
// not the frontend host. In non-local envs the SPA (CloudFront/S3) and the
// API (ALB) live on separate hostnames; a bare same-origin /api/* path hits
// the SPA bucket and gets rewritten to index.html by the SPA 404 fallback.
// Development: http://localhost:3030
// Staging:     https://env-7f3a.outdoorguides.com  (NOT env-9b2d — that's the SPA host)
// Production:  https://api.outdoorguides.com
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3030';

/**
 * Make an API request with authentication.
 *
 * Proactively refreshes the RS256 platform access token when it is within
 * 60 seconds of expiry (via tokenRefreshInterceptor). On a 401 response the
 * token is force-refreshed and the request is retried once. On unrecoverable
 * 401, tokens are cleared and auth:logout is dispatched.
 *
 * @param {string} endpoint - API endpoint (e.g., '/api/v1/auth/login')
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
export async function apiRequest(endpoint, options = {}) {
  // Construct full URL with API base URL
  const url = `${API_BASE_URL}${endpoint}`;

  // Extract timeout and external signal before spreading options into config
  const { timeout = 30000, signal: externalSignal, ...fetchOptions } = options;

  // AbortController for request timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  // Propagate external abort signal (for manual cancellation by caller)
  if (externalSignal) {
    externalSignal.addEventListener('abort', () => controller.abort());
  }

  // Auto-stringify plain object bodies so callers don't need to call JSON.stringify().
  // FormData and already-stringified bodies pass through unchanged.
  const body = fetchOptions.body;
  const serializedBody = (body && !(body instanceof FormData) && typeof body !== 'string')
    ? JSON.stringify(body)
    : body;

  const config = {
    ...fetchOptions,
    body: serializedBody,
    headers: {
      ...(!(body instanceof FormData) && { 'Content-Type': 'application/json' }),
      ...fetchOptions.headers,
    },
    credentials: 'include', // Send cookies (session ID) with requests
    signal: controller.signal,
  };

  try {
    // Proactive token refresh: if the access token is expiring soon, refresh
    // it before sending the request. Auth endpoints are excluded to avoid a
    // redundant call on /auth/refresh itself.
    //
    // The sync-then-async pattern preserves the existing timeout test contract:
    // only enter the async path when a refresh is actually needed so the fetch
    // call is reached synchronously in the common case (no token / valid token).
    const isAuthEndpoint = endpoint.includes('/auth/');
    const storedToken = getAccessToken();
    let accessToken = storedToken;

    if (!isAuthEndpoint && storedToken && isTokenExpiringSoon(storedToken)) {
      // Async: token is expiring — getValidAccessToken handles the singleton
      // refresh promise so concurrent requests queue behind one refresh call.
      accessToken = await getValidAccessToken();
    }

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    let response = await fetch(url, config);

    // On 401: force-refresh the token and retry the original request once.
    // Auth endpoints are excluded (login/refresh handle their own error paths).
    if (response.status === 401 &&
        !endpoint.includes('/auth/refresh') &&
        !endpoint.includes('/auth/login') &&
        !config._retried) {
      config._retried = true;
      const newToken = await forceRefresh();
      config.headers.Authorization = `Bearer ${newToken}`;
      response = await fetch(url, config);
    }

    if (response.status === 429 && window.location.pathname !== '/rate-limited') {
      const retryAfter = response.headers.get('retry-after');
      window.location.href = `/rate-limited${retryAfter ? `?retry=${retryAfter}` : ''}`;
      return new Promise(() => {});
    }

    if (response.status === 502 || response.status === 503 || response.status === 504) {
      window.dispatchEvent(new Event('api:failure'));
    } else if (response.ok) {
      window.dispatchEvent(new Event('api:success'));
    }

    // Parse JSON response with error handling for malformed responses
    let data;
    try {
      data = await response.json();
    } catch (jsonError) {
      // Handle empty or malformed JSON responses
      const parseError = new Error(`Invalid JSON response from ${endpoint}`);
      parseError.status = response.status;
      parseError.statusText = response.statusText;
      parseError.originalError = jsonError.message;

      // Log JSON parse errors to centralized logging
      errorLoggingService.logAPIError(parseError, {
        url: endpoint,
        method: config.method || 'GET',
        status: response.status,
        statusText: response.statusText,
      });

      throw parseError;
    }

    if (!response.ok) {
      const apiError = new Error(data.message || data.error || 'Request failed');
      apiError.status = response.status;
      apiError.statusText = response.statusText;

      // Include field-specific validation errors if present
      if (data.fields) {
        apiError.fields = data.fields;
      }

      // SCRUM-823: Include validation error details array
      if (data.details) {
        apiError.details = data.details;
      }

      // Include original error message if different from Error message
      if (data.error) {
        apiError.error = data.error;
      }

      // Log API error to error logging service
      // Skip 401/403 - these are expected when user is logged out or unauthorized
      if (response.status !== 401 && response.status !== 403) {
        errorLoggingService.logAPIError(apiError, {
          url: endpoint,
          method: config.method || 'GET',
          status: response.status,
          statusText: response.statusText,
        });
      }

      throw apiError;
    }

    return data;
  } catch (error) {
    // Translate AbortController timeout into a recognizable 408 error
    if (error.name === 'AbortError') {
      window.dispatchEvent(new Event('api:failure'));
      const timeoutError = new Error(`Request timeout after ${timeout}ms`);
      timeoutError.status = 408;
      throw timeoutError;
    }

    if (!error.status || error.status >= 500) {
      window.dispatchEvent(new Event('api:failure'));
    }

    // Log network errors (fetch failures, JSON parse errors, etc.)
    // Skip 401/403 - these are expected when user is logged out or unauthorized
    if (!error.status || (error.status !== 401 && error.status !== 403)) {
      errorLoggingService.logAPIError(error, {
        url: endpoint,
        method: config.method || 'GET',
        status: error.status || 0,
        statusText: error.statusText || 'Network Error',
      });
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

export default apiRequest;
